import { useCallback, useState } from 'react';
import type { PickingInfo } from 'deck.gl';
import type { Infrastructure } from '../mapModes/MapModeStrategy';
import type { SensorDevice } from '@/features/sensorMaintenanceLens';

export type InfrastructureHoverObject = Partial<Infrastructure> & {
	_tooltipType?: 'dma';
	dmaName?: string | number | null;
	dmaId?: string | number | null;
};

export type SensorHoverObject = SensorDevice & { _tooltipType: 'sensor' };

export type MapHoverObject = InfrastructureHoverObject | SensorHoverObject;

export interface HoverInfo {
	object: MapHoverObject;
	x: number;
	y: number;
}

export function useMapHover() {
	const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);

	const handleIconHover = useCallback((info: PickingInfo) => {
		if (info.object) {
			setHoverInfo({
				object: info.object,
				x: info.x,
				y: info.y,
			});
		} else {
			setHoverInfo(null);
		}
	}, []);

	const handleDmaHover = useCallback((info: PickingInfo) => {
		if (info.object && info.layer?.id === 'dma-boundary') {
			setHoverInfo({
				object: info.object,
				x: info.x,
				y: info.y,
			});
		} else {
			setHoverInfo((prev) => (prev?.object?._tooltipType === 'dma' ? null : prev));
		}
	}, []);

	const handleSensorHover = useCallback((device: SensorDevice | null, x: number, y: number) => {
		if (device) {
			setHoverInfo({ object: { ...device, _tooltipType: 'sensor' }, x, y });
		} else {
			setHoverInfo((prev) => (prev?.object?._tooltipType === 'sensor' ? null : prev));
		}
	}, []);

	const clearHover = useCallback(() => {
		setHoverInfo(null);
	}, []);

	return { hoverInfo, clearHover, handleIconHover, handleDmaHover, handleSensorHover };
}
